import { Button } from "@chakra-ui/button";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/modal";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { isValidEmail } from "../../util/isValidEmail";

interface UpdateCardEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (emailAddress: string) => Promise<void>;
  currentEmailAddress?: string;
}

export const UpdateCardEmailDialog = ({
  isOpen,
  onClose,
  onConfirm,
  currentEmailAddress = "",
}: UpdateCardEmailDialogProps) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setEmailAddress(currentEmailAddress);
      setIsLoading(false);
      setValidationError(undefined);
    }
  }, [isOpen, currentEmailAddress]);

  const handleConfirm = async () => {
    if (!emailAddress || emailAddress.trim().length === 0) {
      setValidationError("Email address is required");
      return;
    }

    if (!isValidEmail(emailAddress)) {
      setValidationError("Invalid email address");
      return;
    }

    setValidationError(undefined);
    setIsLoading(true);
    try {
      await onConfirm(emailAddress);
      onClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isLoading) {
      handleConfirm();
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
    if (validationError) {
      setValidationError(undefined);
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Update Email Address</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <FormControl isInvalid={!!validationError}>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={emailAddress}
                onChange={handleEmailChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter email address"
                isDisabled={isLoading}
              />
              {validationError && (
                <FormErrorMessage>{validationError}</FormErrorMessage>
              )}
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="cherryButton"
              ml={3}
              onClick={handleConfirm}
              isDisabled={isLoading}
              isLoading={isLoading}
            >
              Update
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
